/**
 * La recherche de notion, en langage courant.
 *
 * LE DÉFAUT QU'ELLE CORRIGE. La recherche exigeait une sous-chaîne exacte du
 * libellé : « division » ne trouvait rien, parce qu'aucun chapitre ne contient
 * ce mot — mais « divis » trouvait « Multiples, diviseurs et divisibilité ».
 * Mesuré en production : « division », « addition », « multiplication »,
 * « tables de multiplication » renvoyaient tous « Rien en 6e ».
 *
 * DEUX MÉCANISMES, ET ILS NE FONT PAS LA MÊME CHOSE.
 *
 * La RACINISATION ne gère que les variantes naturelles d'un même mot :
 * addition/additions, pourcentage/pourcentages, fraction/fractions. Elle est
 * volontairement timide — un radical d'au moins quatre lettres, comparé en
 * DÉBUT de mot et non n'importe où dans la chaîne. Sans cette prudence,
 * « aire » attraperait « air » et « multiplication » attraperait « Multiples ».
 *
 * Les ÉQUIVALENCES sont une table écrite à la main. C'est là que vivent les
 * relations PÉDAGOGIQUES, qu'aucune racine ne peut deviner : « division » et
 * « diviseurs » ne se ressemblent pas par hasard, mais aucun algorithme ne
 * doit inventer ce lien. Trente entrées, relisibles d'un coup d'œil, et rien
 * de flou : pas de distance d'édition, pas de réseau, pas de modèle.
 */

//: Les mots-outils qu'on ignore : « les fractions » doit valoir « fractions ».
const MOTS_OUTILS = new Set([
  "le", "la", "les", "l", "de", "des", "du", "d", "un", "une", "et", "en",
  "a", "au", "aux", "sur", "mon", "ma", "mes", "pour", "avec", "dans",
]);

//: Ce que l'enseignant ou le parent tape → ce que le catalogue nomme.
//:
//: À GAUCHE le vocabulaire courant, à DROITE le vocabulaire des chapitres.
//: On ajoute une ligne quand un mot manque ; on n'écrit jamais de règle
//: automatique pour les deviner.
export const EQUIVALENCES = {
  division: ["divis", "quotient", "partage"],
  diviser: ["divis", "quotient"],
  partage: ["divis", "fraction", "partag"],
  partager: ["divis", "fraction", "partag"],
  addition: ["addit", "somme"],
  additionner: ["addit", "somme"],
  plus: ["addit", "somme"],
  somme: ["addit", "somme"],
  soustraction: ["soustrac", "differenc"],
  soustraire: ["soustrac", "differenc"],
  moins: ["soustrac", "differenc"],
  difference: ["soustrac", "differenc"],
  //: « table » est volontairement absent : il ouvrait « Tableaux »
  //: dans « Proportionnalité — Tableaux, coefficient ».
  multiplication: ["multiplic", "produit"],
  multiplier: ["multiplic", "produit"],
  fois: ["multiplic", "produit"],
  table: ["multiplic"],
  tables: ["multiplic"],
  produit: ["multiplic", "produit"],
  pourcentage: ["pourcent", "proportion"],
  pourcent: ["pourcent", "proportion"],
  moyenne: ["moyenn", "statistiq"],
  aire: ["aire", "surfac"],
  surface: ["aire", "surfac"],
  perimetre: ["perimetr", "longueur"],
  volume: ["volum", "capacit"],
  angle: ["angl"],
  heure: ["heur", "duree", "temps"],
  duree: ["duree", "heur", "temps"],
  monnaie: ["monnai", "euro", "prix"],
  probabilite: ["probabilit", "hasard", "chance"],
  equation: ["equation", "inconnu"],
  puissance: ["puissanc", "exposant"],
};

/** Minuscules, sans accents, sans ponctuation. */
export const normaliser = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

//: Suffixes de variantes naturelles, du plus long au plus court.
const SUFFIXES = ["ements", "ement", "ations", "ation", "tions", "sions",
                  "euses", "eurs", "ives", "aux", "es", "s", "x"];

/**
 * Le radical d'un mot, pour rapprocher « additions » de « addition ».
 * Jamais en dessous de quatre lettres : « aires » doit donner « aire », pas
 * « air », sans quoi la recherche se met à répondre n'importe quoi.
 */
export function radical(mot) {
  const m = normaliser(mot);
  if (m.length <= 4) return m;
  for (const suf of SUFFIXES) {
    if (m.endsWith(suf) && m.length - suf.length >= 4) {
      return m.slice(0, m.length - suf.length);
    }
  }
  return m;
}

/** Les mots utiles d'une requête, mots-outils écartés. */
export function motsDeLaRequete(requete) {
  return normaliser(requete).split(/[^a-z0-9]+/).filter((t) => t && !MOTS_OUTILS.has(t));
}

/**
 * Un chapitre répond-il à ce mot ?
 *
 * Trois façons, dans l'ordre : le mot figure tel quel ; l'une de ses
 * équivalences pédagogiques ouvre un mot du chapitre ; son radical ouvre un
 * mot du chapitre. Toujours en DÉBUT de mot — jamais au milieu.
 */
function motCorrespond(mot, motsDuChapitre) {
  // TOUJOURS en début de mot, jamais au milieu. Une comparaison par
  // sous-chaîne faisait répondre « Vocabulaire » à « aire » — mesuré.
  const ouvre = (racine) =>
    racine.length >= 3 && motsDuChapitre.some((m) => m.startsWith(racine));
  if (ouvre(mot)) return true;
  const equivalents = EQUIVALENCES[mot] || EQUIVALENCES[radical(mot)];
  if (equivalents && equivalents.some(ouvre)) return true;
  return ouvre(radical(mot));
}

/**
 * Les chapitres qui répondent à la requête. Tous les mots doivent répondre :
 * « tables de multiplication » ne doit pas ramener tout ce qui parle de table.
 */
export function chercherChapitres(chapitres, requete) {
  const mots = motsDeLaRequete(requete);
  if (mots.length === 0) return null;
  return (chapitres || []).filter((c) => {
    const texte = normaliser(`${c.libelle} ${c.domaine || ""} ${(c.tags || []).join(" ")}`);
    const motsDuChapitre = texte.split(/[^a-z0-9]+/).filter(Boolean);
    return mots.every((m) => motCorrespond(m, motsDuChapitre));
  });
}
